import { Reveal } from "@/components/motion/Reveal";

const chapters = [
  { title: "Entendemos la organización", desc: "Analizamos el contexto, necesidades, procesos, riesgos, partes interesadas y objetivos estratégicos." },
  { title: "Diagnosticamos la situación actual", desc: "Determinamos el nivel de cumplimiento, fortalezas, oportunidades de mejora y brechas existentes." },
  { title: "Diseñamos una solución personalizada", desc: "Creamos un plan de trabajo realista, priorizado y adaptado a los recursos de la organización." },
  { title: "Implementamos junto al cliente", desc: "Trabajamos de forma colaborativa con los equipos responsables, evitando soluciones genéricas o difíciles de mantener." },
  { title: "Verificamos los resultados", desc: "Evaluamos indicadores, cumplimiento, eficacia de los controles y avance de los planes de acción." },
  { title: "Fortalecemos la mejora continua", desc: "Acompañamos a la organización para que el sistema se mantenga actualizado y genere resultados sostenibles." },
];

const phva = ["Planificar", "Hacer", "Verificar", "Actuar"];

export default function Methodology() {
  return (
    <section id="metodologia" className="relative py-24 md:py-36 border-t border-[#1A1A1A]/10" data-testid="methodology-section">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="grid grid-cols-12 gap-6 items-end mb-14">
          <Reveal className="col-span-12 md:col-span-8">
            <div className="eyebrow mb-6">— 03 · Metodología</div>
            <h2 className="display-2">Nuestra metodología de <em className="italic text-[#1B3B2B]">mejora continua</em>.</h2>
          </Reveal>
          <Reveal delay={0.15} className="col-span-12 md:col-span-4">
            <p className="text-sm text-[#4A4A4A] leading-relaxed">
              Basamos nuestro trabajo en principios de mejora continua y en el ciclo PHVA aplicado a cada intervención.
            </p>
          </Reveal>
        </div>

        {/* PHVA circular diagram */}
        <div className="mt-6 flex flex-col md:flex-row items-center gap-10 md:gap-16 border-t border-b border-[#1A1A1A]/10 py-14">
          <div className="relative w-[280px] h-[280px] md:w-[360px] md:h-[360px] shrink-0" data-testid="phva-diagram">
            <div className="absolute inset-0 rounded-full border border-[#1A1A1A]/20" />
            <div className="absolute inset-6 rounded-full border border-[#1A1A1A]/10" />
            {phva.map((label, i) => {
              const angle = (i / phva.length) * Math.PI * 2 - Math.PI / 2;
              const r = 132;
              const x = 50 + (Math.cos(angle) * r) / 3.6;
              const y = 50 + (Math.sin(angle) * r) / 3.6;
              return (
                <div
                  key={label}
                  className="absolute flex flex-col items-center justify-center text-center"
                  style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%, -50%)" }}
                >
                  <div className="chapter-num text-[1.75rem] leading-none">{String(i + 1).padStart(2, "0")}</div>
                  <div className="mt-1 font-serif text-lg text-[#1A1A1A]">{label}</div>
                </div>
              );
            })}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="font-serif italic text-2xl text-[#1B3B2B]">PHVA</div>
            </div>
          </div>

          <div className="flex-1">
            <p className="font-serif text-2xl md:text-3xl leading-tight text-[#1A1A1A]">
              “No buscamos crear documentación innecesaria. Diseñamos sistemas prácticos, funcionales y alineados con la realidad de cada organización.”
            </p>
            <p className="mt-6 text-sm text-[#4A4A4A] max-w-xl leading-relaxed">
              Cada intervención sigue un ciclo de aprendizaje: planificar los cambios, ejecutarlos, verificar resultados y actuar sobre las oportunidades detectadas. Así construimos mejora sostenible en el tiempo.
            </p>
          </div>
        </div>

        {/* Chapters manifesto */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-0 border-t border-l border-[#1A1A1A]/12">
          {chapters.map((c, i) => (
            <Reveal
              key={c.title}
              delay={i * 0.06}
              className={i % 2 === 0 ? "md:col-span-7" : "md:col-span-5"}
            >
              <article className="border-r border-b border-[#1A1A1A]/12 p-8 md:p-10 h-full hover:bg-[#EFEBE2] transition-colors" data-testid={`method-chapter-${i}`}>
                <div className="flex items-baseline gap-4">
                  <div className="chapter-num text-[3rem]">{String(i + 1).padStart(2, "0")}</div>
                  <div className="eyebrow">Etapa</div>
                </div>
                <h3 className="mt-4 font-serif text-3xl md:text-4xl leading-tight">{c.title}</h3>
                <p className="mt-4 text-sm text-[#4A4A4A] leading-relaxed max-w-lg">{c.desc}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
